/**
 * Desktop I/O bridge — app-data file operations (Faz 9.1/9.2/9.4).
 *
 * Two hosts, one API:
 * - Tauri desktop: `window.__TAURI__.core.invoke` over the shell's built-in
 *   fsio commands (main.rs). The atomic write retries Windows sharing
 *   violations 10 × 200 ms inside Rust (raw_os_error 32/33), mirrors OCS
 *   `replace_save_file`, and produces the `.bak` copy.
 * - Plain browser: the Origin Private File System (OPFS) under a mirrored
 *   `yap/` tree — same paths, no appData available. Writes are chunked so
 *   multi-megabyte autosave envelopes never trip a single-call limit.
 *
 * There is deliberately no localStorage path here: the 3.5M-char snapshot
 * cap (Faz 9 §1) is gone; oversized documents stream to real files.
 */

export const APP_ROOT_DIR = 'yap';
export const AUTOSAVE_DIR = 'yap/autosave';
export const RECOVERY_LOG_DIR = 'yap/recovery_logs';

interface TauriCore {
    invoke?: (cmd: string, args?: Record<string, unknown>) => Promise<unknown>;
}
interface TauriGlobal {
    core?: TauriCore;
}

/** True when the desktop shell injected the Tauri global API. */
export function isTauriHost(): boolean {
    return typeof window !== 'undefined'
        && !!(window as unknown as { __TAURI__?: TauriGlobal }).__TAURI__?.core?.invoke;
}

async function tauriInvoke<T>(cmd: string, args: Record<string, unknown>): Promise<T> {
    const core = (window as unknown as { __TAURI__?: TauriGlobal }).__TAURI__?.core;
    if (!core?.invoke) throw new Error('tauri-core-unavailable');
    return core.invoke(cmd, args) as Promise<T>;
}

/** `<appDataDir>` from the Tauri path resolver (core:path:default). */
export async function appDataRoot(): Promise<string | null> {
    if (!isTauriHost()) return null;
    try {
        return await tauriInvoke<string>('fsio_app_data_dir', {});
    } catch {
        return null;
    }
}

/** `<appDataDir>/yap/autosave` — the sv$ folder on the desktop host. */
export async function appDataAutosaveDir(): Promise<string | null> {
    const base = await appDataRoot();
    if (!base) return null;
    const sep = base.includes('\\') ? '\\' : '/';
    return `${base.replace(/[\\/]+$/, '')}${sep}${APP_ROOT_DIR}${sep}autosave`;
}

/** `<appDataDir>/yap/recovery_logs` — plain-text recovery reports. */
export async function appDataRecoveryLogDir(): Promise<string | null> {
    const base = await appDataRoot();
    if (!base) return null;
    const sep = base.includes('\\') ? '\\' : '/';
    return `${base.replace(/[\\/]+$/, '')}${sep}${APP_ROOT_DIR}${sep}recovery_logs`;
}

// ---------------------------------------------------------------------------
// OPFS fallback (plain browser host)
// ---------------------------------------------------------------------------

async function opfsDirHandle(segments: string[]): Promise<FileSystemDirectoryHandle> {
    let dir = await navigator.storage.getDirectory();
    for (const seg of segments) {
        dir = await dir.getDirectoryHandle(seg, { create: true });
    }
    return dir;
}

async function opfsFileHandle(
    relPath: string,
    create: boolean
): Promise<FileSystemFileHandle | null> {
    const segments = relPath.split('/').filter(Boolean);
    const name = segments.pop();
    if (!name) return null;
    let dir: FileSystemDirectoryHandle;
    try {
        dir = await opfsDirHandle(segments);
    } catch {
        return null;
    }
    try {
        return await dir.getFileHandle(name, { create });
    } catch {
        return null;
    }
}

/** Writes text in ~1 MiB chunks so huge envelopes stream, not stall. */
async function opfsWriteText(relPath: string, text: string): Promise<void> {
    const handle = await opfsFileHandle(relPath, true);
    if (!handle) throw new Error('opfs-unavailable');
    const writable = await handle.createWritable();
    const encoder = new TextEncoder();
    const CHUNK = 1_048_576;
    let offset = 0;
    while (offset < text.length) {
        const slice = text.slice(offset, offset + CHUNK);
        await writable.write(encoder.encode(slice));
        offset += CHUNK;
    }
    await writable.close();
}

async function opfsReadText(relPath: string): Promise<string | null> {
    const handle = await opfsFileHandle(relPath, false);
    if (!handle) return null;
    const file = await handle.getFile();
    return file.text();
}

async function opfsDelete(relPath: string): Promise<boolean> {
    const segments = relPath.split('/').filter(Boolean);
    const name = segments.pop();
    if (!name) return false;
    let dir: FileSystemDirectoryHandle;
    try {
        dir = await opfsDirHandle(segments);
    } catch {
        return false;
    }
    try {
        await dir.removeEntry(name);
        return true;
    } catch {
        return false;
    }
}

async function opfsListDir(relPath: string): Promise<string[]> {
    const segments = relPath.split('/').filter(Boolean);
    let dir: FileSystemDirectoryHandle;
    try {
        dir = await opfsDirHandle(segments);
    } catch {
        return [];
    }
    const names: string[] = [];
    // Modern OPFS entries() iterator; guarded so older builds degrade to [].
    const iterator = (dir as unknown as {
        entries?: () => AsyncIterableIterator<[string, FileSystemHandle]>;
    }).entries;
    if (!iterator) return names;
    try {
        for await (const [name, handle] of iterator.call(dir)) {
            if (handle.kind === 'file') names.push(name);
        }
    } catch {
        return names;
    }
    names.sort();
    return names;
}

// ---------------------------------------------------------------------------
// Unified file API (Tauri appData when available, OPFS otherwise)
// ---------------------------------------------------------------------------

/** Writes a text file under `yap/<relPath>` atomically on desktop. */
export async function writeAppFile(relPath: string, text: string, atomic = false): Promise<void> {
    if (isTauriHost()) {
        const base = await appDataRoot();
        if (base) {
            const sep = base.includes('\\') ? '\\' : '/';
            const full = `${base.replace(/[\\/]+$/, '')}${sep}${APP_ROOT_DIR}${sep}${relPath.replace(/\//g, sep)}`;
            if (atomic) {
                await tauriInvoke('fsio_write_atomic', { path: full, contents: text });
            } else {
                await tauriInvoke('fsio_write_text', { path: full, contents: text });
            }
            return;
        }
    }
    await opfsWriteText(relPath, text);
}

/** Reads `yap/<relPath>`; null when the file does not exist. */
export async function readAppFile(relPath: string): Promise<string | null> {
    if (isTauriHost()) {
        const base = await appDataRoot();
        if (base) {
            const sep = base.includes('\\') ? '\\' : '/';
            const full = `${base.replace(/[\\/]+$/, '')}${sep}${APP_ROOT_DIR}${sep}${relPath.replace(/\//g, sep)}`;
            return tauriInvoke<string | null>('fsio_read_text', { path: full });
        }
    }
    return opfsReadText(relPath);
}

/** Deletes `yap/<relPath>` (best-effort; resolves false when absent/locked). */
export async function deleteAppFile(relPath: string): Promise<boolean> {
    if (isTauriHost()) {
        const base = await appDataRoot();
        if (base) {
            const sep = base.includes('\\') ? '\\' : '/';
            const full = `${base.replace(/[\\/]+$/, '')}${sep}${APP_ROOT_DIR}${sep}${relPath.replace(/\//g, sep)}`;
            try {
                return await tauriInvoke<boolean>('fsio_delete', { path: full });
            } catch {
                return false;
            }
        }
    }
    return opfsDelete(relPath);
}

/** Lists file names under `yap/<relPath>` (non-recursive). */
export async function listAppFiles(relPath: string): Promise<string[]> {
    if (isTauriHost()) {
        const base = await appDataRoot();
        if (base) {
            const sep = base.includes('\\') ? '\\' : '/';
            const full = `${base.replace(/[\\/]+$/, '')}${sep}${APP_ROOT_DIR}${sep}${relPath.replace(/\//g, sep)}`;
            try {
                return await tauriInvoke<string[]>('fsio_list_dir', { path: full });
            } catch {
                return [];
            }
        }
    }
    return opfsListDir(relPath);
}

/** True when `yap/<relPath>` exists. */
export async function appFileExists(relPath: string): Promise<boolean> {
    if (isTauriHost()) {
        const base = await appDataRoot();
        if (base) {
            const sep = base.includes('\\') ? '\\' : '/';
            const full = `${base.replace(/[\\/]+$/, '')}${sep}${APP_ROOT_DIR}${sep}${relPath.replace(/\//g, sep)}`;
            try {
                return await tauriInvoke<boolean>('fsio_exists', { path: full });
            } catch {
                return false;
            }
        }
    }
    return (await opfsReadText(relPath)) !== null;
}
